<template>
    <svg ref="container" :width="width" :height="height" :viewBox="`${arWidth} ${arHeight} ${width} ${height}`">
        <g v-for="(elem, index) in sortedValues" :key="index">
            <line
                :x1="chartData[index].lineX1"
                :y1="chartData[index].lineY1"
                :x2="chartData[index].lineX2"
                :y2="chartData[index].lineY2"
                :style="`stroke:#A0CA6E;stroke-width:1`"
            ></line>
            <circle
                :cx="width / 2"
                :cy="height / 2"
                :r="radius"
                :stroke="colors[index]"
                :stroke-width="strokeWidth"
                :stroke-dasharray="adjustedCircumference"
                :stroke-dashoffset="calculateStrokeDashOffset(elem.val, circumference)"
                :transform="returnCircleTransformValue(index)"
                fill="transparent"
            ></circle>
            <text
                :ref="`text-${index}`"
                :text-anchor="chartData[index].textPos"
                :y="chartData[index].textY"
                fill="#E3C855"
            >
                <tspan :x="chartData[index].textX">{{ elem.name }}</tspan>
                <tspan :x="chartData[index].textX" dy="22">{{ percentageLabel(elem.val) }}</tspan>
            </text>
        </g>
    </svg>
</template>

<script>
export default {
    name: "Donut",
    props: {
        initialValues: Array,
        initialLabels: Array,
        mobile: Boolean,
        colors: Array,
        expand: Boolean,
    },
    watch: {
        expand: function() {
            this.updateDOM();
        },
    },
    computed: {
        adjustedCircumference() {
            return this.circumference - 1;
        },
        circumference() {
            return 2 * Math.PI * this.radius;
        },
        dataTotal() {
            let total = 0;
            this.sortedValues.forEach((elem) => {
                total += elem.val;
            });
            return total;
        },
    },
    methods: {
        calculateCoords(dataVal, angleOffset) {
            const angle = (this.dataPercentage(dataVal) * 360) / 2 + angleOffset;
            const radians = this.degreesToRadians(angle);

            const lineOffset = this.radius * (2 / 3);
            const lineLength = 20;
            const textScale = this.mobile ? this.radius + lineLength : this.radius + lineLength;

            const onLeft = Math.cos(radians) < 0;
            const coords = {
                x: (this.radius + textScale) * Math.cos(radians) + this.width / 2,
                y: (this.radius + textScale) * Math.sin(radians) + this.height / 2 - 10,
                x1: (this.radius + lineOffset) * Math.cos(radians) + this.width / 2,
                y1: (this.radius + lineOffset) * Math.sin(radians) + this.height / 2,
                x2: (this.radius + lineOffset + lineLength) * Math.cos(radians) + this.width / 2,
                y2: (this.radius + lineOffset + lineLength) * Math.sin(radians) + this.height / 2,
                textPos: onLeft ? "end" : "start",
            };

            return coords;
        },
        calculateStrokeDashOffset(dataVal, circumference) {
            const strokeDiff = this.dataPercentage(dataVal) * circumference;
            return circumference - strokeDiff;
        },
        dataPercentage(dataVal) {
            return dataVal / this.dataTotal;
        },
        degreesToRadians(angle) {
            return angle * (Math.PI / 180);
        },
        percentageLabel(dataVal) {
            return `${Math.round(this.dataPercentage(dataVal) * 100)}%`;
        },
        returnCircleTransformValue(index) {
            return `rotate(${this.chartData[index].degrees}, ${this.width / 2}, ${this.height / 2})`;
        },
        calculateChartData() {
            this.chartData = [];
            this.sortedValues.forEach((dataVal) => {
                const { x, y, x1, y1, x2, y2, textPos } = this.calculateCoords(dataVal.val, this.angleOffset);
                const data = {
                    degrees: this.angleOffset,
                    textX: x,
                    textY: y,
                    lineX1: x1,
                    lineY1: y1,
                    lineX2: x2,
                    lineY2: y2,
                    textPos: textPos,
                };
                this.chartData.push(data);
                this.angleOffset = this.dataPercentage(dataVal.val) * 360 + this.angleOffset;
            });
        },
        sortInitialValues() {
            let data = this.initialValues.map((val, idx) => {
                return { name: this.initialLabels[idx], val: val };
            });
            this.sortedValues = data.sort((a, b) => b.val - a.val);
        },
        updateDOM() {
            let minX = Number.MAX_VALUE,
                minY = Number.MAX_VALUE;
            let maxX = Number.MIN_VALUE,
                maxY = Number.MIN_VALUE;
            for (let i = 0; i < this.sortedValues.length; i++) {
                let { x, y, width, height } = this.$refs[`text-${i}`][0].getBoundingClientRect();
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x + width);
                maxY = Math.max(maxY, y + height);
            }

            if (maxX - minX < 2) return;

            if (maxX - minX > this.width && this.width <= 400 && !this.mobile) {
                this.width = 450;
                this.$forceUpdate();
                this.calculateChartData();
                this.updateDOM();
                return;
            }

            let { x: cx, y: cy, width: cwidth, height: cheight } = this.$refs.container.getBoundingClientRect();
            if (maxX - minX > cwidth) {
                this.radius = 50;
                this.strokeWidth = 70;
                this.calculateChartData();
                return;
            }

            if (maxX > cx + cwidth) {
                this.arWidth = maxX - (cx + cwidth);
            }

            if (minX < cx) {
                this.arWidth = minX - cx;
            }

            if (maxY > cy + cheight) {
                this.arHeight += maxY - (cy + cheight);
            }

            if (minY < cy) {
                this.arHeight = minY - cy;
            }
        },
    },
    mounted() {
        this.sortInitialValues();
        this.calculateChartData();

        let _this = this;
        this.$nextTick(() => window.addEventListener("resize", _this.updateDOM()));
    },
    data() {
        console.log(this.initialLabels);
        console.log(this.initialValues);
        return {
            angleOffset: -100,
            chartData: [],
            radius: this.mobile ? 50 : 60,
            strokeWidth: this.mobile ? 70 : 80,
            sortedValues: [],
            // default size if not specified
            arWidth: 0,
            arHeight: 0,
            height: this.mobile ? 300 : 400,
            width: this.mobile ? 450 : 400,
        };
    },
};
</script>
